import axios from "axios";
import { DollarSign, EllipsisVertical, Loader, MessageCirclePlus, WandSparkles, MessageSquareX as MessageSquareXIcon } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { useCurrentUser } from "base/app";
import { MILLIS_IN_A_DAY } from "base/constants";
import EventTracker, { PAGE_EVENTS } from "base/eventsTracker";
import { WalletLedgerEntries } from "base/payments";
import {
    ChatSession, INBOX_MESSAGE_TYPE_WA_INCOMING_MSG,
    INBOX_MESSAGE_TYPE_WA_MSG,
    INBOX_MESSAGE_TYPE_WA_REPLY_MSG,
    SESSION_USER_ADMIN, SimpleMessageContextMenu
} from "base/ui/chat";
import { DateView } from "base/ui/date";
import { GenericException } from "base/ui/errors";
import { SelectTags } from "base/ui/misc";
import { Popup } from "base/ui/popups";
import { idToTitle } from "base/utils";
import { getRandomColor, useRerender, loadScript } from "base/utils/common";
import { broadcaster } from "base/utils/events";

import AddNewCustomer from "./AddNewCustomer";
import { getPrice } from "../../../helpers/StoreHelper";
import PointsIcon from '../../../images/ic_points.svg?url';
import { FormErrorText } from "../../ui/FormErrorText";

const STATUS_COLORS_LIST = [
    '#800080', // Purple (alternate)
    '#008080', // Teal
    '#808000', // Olive
    '#800000', // Maroon
    '#115da9', // Oxford Blue
    '#6A2150', // Purple
]

broadcaster.add_event_listener("chat:message_sent", (_im, session_data) => {
	if (session_data.session_id.startsWith("customer_notes_")) {
		EventTracker.track(PAGE_EVENTS.USER_NOTES_SUCCESS);
	}
});


const delivery_route_users_cache = {};
const delivery_route_pending_resolves = {};
const to_fetch_delivery_route_user_ids = {};
function getDeliveryRouteUsersCached(delivery_route_id, user_ids) {
    let cached_users = delivery_route_users_cache[delivery_route_id];
    if (!cached_users) {
        cached_users = {};
        delivery_route_users_cache[delivery_route_id] = cached_users;
    }

    if(!to_fetch_delivery_route_user_ids[delivery_route_id]){
        to_fetch_delivery_route_user_ids[delivery_route_id] = new Set();
    }

    const uncached_user_ids = [];
    user_ids.forEach((user_id) => { !cached_users[user_id] && uncached_user_ids.push(user_id) });

    if(!uncached_user_ids.length){
        return Promise.resolve(cached_users);
    }
    uncached_user_ids.forEach((user_id) => to_fetch_delivery_route_user_ids[delivery_route_id].add(user_id)); // pool in uncached ids
    
    if(delivery_route_pending_resolves[delivery_route_id]){ // others are waiting
        return new Promise((resolve) => {
            delivery_route_pending_resolves[delivery_route_id].push(resolve);
        });
    }    
    setTimeout(() => {
        const to_fetch_user_ids = to_fetch_delivery_route_user_ids[delivery_route_id];
        /* ids requested after this point start a new batch, they are not part of this request */
        const pending_resolves = delivery_route_pending_resolves[delivery_route_id];
        to_fetch_delivery_route_user_ids[delivery_route_id] = null;
        delivery_route_pending_resolves[delivery_route_id] = null;
        axios.post(`/api/admin/users/${delivery_route_id}`, {
            "action": "search",
            "user_ids": [...to_fetch_user_ids]
        }).then((resp) => {
            resp.data.users?.forEach((user) => {
                cached_users[user._id] = user;
            });
        }).finally(() => {
            pending_resolves.forEach((resolve) => resolve(cached_users));
        });
    }, 100);

    delivery_route_pending_resolves[delivery_route_id] = [];
    return new Promise((resolve) => {
        delivery_route_pending_resolves[delivery_route_id].push(resolve);
    });
}

function SimpleMessageContextMenuOptions({ im, session_data }) {
    const items = []
    if(im.data1?.type === "order_request"){
        items.push(
            <div className="w3-padding-8 tw-flex tw-gap-x-1 tw-items-center" 
                key="add_pricing"
                onClick={() => {
                    window.open(`/${im.data1.delivery_route_id}?buying_for_user_id=${im.data1.user_id}&cart_id=${im.data1.cart_id}`)
                }}
            >
                <DollarSign size={18} className="w3-text-amber" />
                Add Pricing
            </div>
        )
    }
    if(session_data.delivery_route_user){
        if(im._type === INBOX_MESSAGE_TYPE_WA_INCOMING_MSG){
            items.push(
                <div className="w3-padding-8 tw-flex tw-gap-x-1 tw-items-center" 
                    onClick={() => {
                        EventTracker.track(PAGE_EVENTS.AI_CART_INITIATED)
                        broadcaster.broadcast_event("chat:scan_items", session_data, im.data.txt)
                    }}
                    key="scan_for_items"
                >
                    <WandSparkles size={18} className="w3-text-amber" />
                    AI Cart
                </div>
            );
        }
        if(im._type === INBOX_MESSAGE_TYPE_WA_REPLY_MSG || im._type === INBOX_MESSAGE_TYPE_WA_MSG){
            const cart_id = im.data?.fb?.interactive?.action?.buttons?.flatMap(
                (button_data) =>  ["checkout:cart:", "cart_address:", "pay_now:cart:"].map(
                    (prefix) => {
                        return button_data?.reply.id?.startsWith(prefix)
                                && button_data?.reply.id.substr(prefix.length).split("_")[0]
                    }
                ).filter((cart_id) => cart_id)
            )[0];

            if(cart_id){
                items.push(
                    <div className="w3-padding-8 tw-flex tw-gap-x-1 tw-items-center" onClick={() => broadcaster.broadcast_event("chat:open_cart", session_data, cart_id)} 
                        key="checkout:cart"
                    >
                        <DollarSign size={18} className="w3-text-amber" />
                        Open Cart
                    </div>
                );
            }
        }
    }
    return items;
}


SimpleMessageContextMenu.Options = SimpleMessageContextMenuOptions;

function SurveyPopup({ delivery_route, user, survey_questions, onSelect }) {
    const ctx = useRef({}).current;
    const [current_index, setCurrentIndex] = useState(0)
    const [error, setError] = useState()

    function submitAnswers() {
        var msg = "\n"
        survey_questions.map((question, i) => {
            msg = msg + (i + 1) + ". " + question.title + "\n"
            msg = msg + "Ans: " + question.answer + "\n"
        })
        const im = {
            payload: msg,
            dest_session_id: `customer_notes_${delivery_route._id}_${user._id}`,
        }

        axios.post("/session/send", im).then(
            (resp) => {
                if (resp.data.errors) {
                    Popup.show("Errors", <GenericException ex={resp.data.errors} />)
                    return;
                }
                axios.post(`/api/admin/user/${delivery_route._id}`,
                    {
                        user_id: user._id,
                        created_at: resp.data.inbox_message.created_at,
                        inbox_id: resp.data.inbox_message.inbox_id,
                        action: 'log_customer_note'
                    }
                )
            }
        )
    }

    return (
        <div className='tw-p-4'>
        {   
            survey_questions && survey_questions?.length 
                ?  <>
                <p className="tw-mt-2">{survey_questions[current_index].title}</p>
                <p className="tw-text-xs tw-text-gray-500">{survey_questions[current_index].description}</p>
                <textarea
                    className="tw-border tw-px-4 tw-py-2 tw-w-full tw-rounded tw-text-md tw-mt-1"
                    type={survey_questions[current_index]._type || "text"}
                    inputMode={survey_questions[current_index]._type === "number" ? "numeric" : "text"}
                    id="survey_answer"
                    defaultValue=""
                    placeholder='Enter your answer here..'
                    onChange={(evt) => {ctx.answer = evt.target.value}}
                />
                {error && <p className="tw-text-xs tw-text-red-500 tw-text-center">{error}</p>}
                <p className="">{(current_index + 1) + "/" + survey_questions.length}</p>
                <button className="tw-mx-4 tw-mb-4 tw-px-4 tw-py-2 tw-bg-yellow-500 tw-rounded tw-text-sm w3-right"
                    type="button"
                    onClick={() => {
                        if (survey_questions[current_index].required && !ctx.answer) {
                            setError("Please enter answer to continue")
                            return
                        }
                        setError("")
                        survey_questions[current_index].answer = ctx.answer || "-"
                        if (current_index === (survey_questions.length - 1)) {
                            submitAnswers()
                            onSelect()
                        } else {
                            document.getElementById("survey_answer").value = ""
                            ctx.answer = ""
                            setCurrentIndex(current_index + 1)
                        }
                    }}>
                    {current_index === (survey_questions.length - 1) ? "Finish" : "Next"}
                </button>
                </>
                : <p>No Questions</p>
        }
        </div>
    );
}

function GiveCredit({ credit_wallet, delivery_route_id, user_id, onUpdate }) {
    const [credit_amount, setCreditAmount] = useState((credit_wallet?.minimum_wallet_amount || 0) * -1);
    const [billing_cycle, setBillingCycle] = useState(credit_wallet?.repayment_period || 'WEEK');

    const [is_loading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);

    const updateCredit = () => {
        if (is_loading) return;
        if (Number.isNaN(credit_amount || credit_amount < 0)) {
            setError('Please enter valid amount.');
            return;
        }
        setIsLoading(true);
        axios.post(
            `/api/admin/give_credit/${delivery_route_id}/${user_id}`,
            { credit_amount, repayment_period: billing_cycle }
        ).then((resp) => {
            if (resp.data.errors) {
                Popup.show("Errors", <GenericException ex={resp.data.errors} />);
                return;
            }
            EventTracker.track(PAGE_EVENTS.GIVE_CREDITS_SUCCESS);
            onUpdate(resp.data.credit_wallet);
        }).finally(() => setIsLoading(false));
    }

    return (
        <div className='tw-pt-4 tw-pb-10'>
            <div className='tw-px-4'>
                <div className='tw-mb-1 tw-text-sm tw-text-gray-600'>Credit Limit</div>
                <input type='number' className='w3-input tw-text-sm tw-outline-0 tw-mb-4' defaultValue={credit_amount}
                    onChange={(evt) => { setCreditAmount(parseInt(evt.target.value, 10)); setError(null) }}
                    placeholder='Amount'
                />
                {error ? <FormErrorText msg={error} /> : null}
                <div className='tw-mb-1 tw-text-sm tw-text-gray-600'>Billing Cycle</div>
                <select className='w3-input tw-text-sm tw-outline-0 tw-mb-2' defaultValue={billing_cycle}
                    onChange={(evt) => setBillingCycle(evt.target.value)}
                >
                    <option value='WEEK'>One Week</option>
                    <option value='FORTNIGHT'>Fortnight</option>
                    <option value='MONTH'>One Month</option>
                </select>
            </div>
            <div className='tw-flex tw-gap-4 tw-absolute tw-bottom-0 tw-w-full tw-p-4'>
                <button className={`btn-primary-v2 tw-gap-x-1 tw-flex tw-items-center hover:tw-flex tw-w-full tw-justify-center ${is_loading ? 'tw-opacity-50' : ''}`}
                    type='button'
                    disabled={is_loading}
                    onClick={updateCredit}>
                    {is_loading ? <Loader className="tw-animate-custom-spin" size={16} /> : null}
                    Save
                </button>
            </div>
        </div>
    )
}

function CollectCreditDue({ credit_wallet, delivery_route_id, user_id, onUpdate }) {
    const [due_amount, setDueAmount] = useState(getPrice(credit_wallet.wallet_currency, credit_wallet.due_amount));

    const [is_loading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);

    const updateCredit = () => {
        if (is_loading) return;
        if (isNaN(due_amount || due_amount < 0)) {
            setError('Please enter valid amount.');
            return;
        }
        setIsLoading(true);
        axios.post(
            `/api/admin/delivery_route/${delivery_route_id}/user/${user_id}/repay_credit`,
            { "repayment_amount": due_amount }
        ).then((resp) => {
            if (resp.data.errors) {
                Popup.show("Errors", <GenericException ex={resp.data.errors} />)
                return;
            }
            onUpdate(resp.data.repaying_user_credit_wallet);
        })
            .finally(() => setIsLoading(false));
    }

    return (
        <div className='tw-pt-4 tw-pb-10'>
            <div className='tw-px-4'>
                <div className='tw-mb-1 tw-text-sm tw-text-gray-600'>Credit Limit</div>
                <input type='number' className='w3-input tw-text-sm tw-outline-0 tw-mb-4' defaultValue={due_amount}
                    disabled={true}
                    onChange={(evt) => { setDueAmount(parseInt(evt.target.value)); setError(null) }}
                    placeholder='Amount'
                />
                {error ? <FormErrorText msg={error} /> : null}
            </div>
            <div className='tw-flex tw-gap-4 tw-absolute tw-bottom-0 tw-w-full tw-p-4'>
                <button className={`btn-primary-v2 tw-gap-x-1 tw-flex tw-items-center hover:tw-flex tw-w-full tw-justify-center ${is_loading ? 'tw-opacity-50' : ''}`}
                    disabled={is_loading}
                    onClick={updateCredit} type="button">
                    {is_loading ?  <Loader size={16} className="tw-animate-custom-spin" /> : null}
                    Save
                </button>
            </div>
        </div>
    )
}

const RefundUser = ({ delivery_route, user, onSave }) => {
    const [amount, setAmount] = useState('');
    const [is_loading, setIsLoading] = useState(false);

    const handleRefund = () => {
        const payload = {
            "amount": amount,
            "wallet_user_id": user._id,
            "wallet_for": `delivery_route_${delivery_route._id}`,
            "wallet_currency": delivery_route.currency
        }
        setIsLoading(true);
        axios.post(`/api/admin/deposit/wallet/${delivery_route._id}`, payload)
            .then(
                (resp) => {
                    if (resp.data.errors) {
                        Popup.show("Errors", <GenericException ex={resp.data.errors} />)
                        return;
                    }
                    user.credit_wallet = resp.data.deposit_wallet
                    onSave(user);
                }
            ).finally(() => setIsLoading(false));
    }

    return (
        <div className="tw-p-4">
            <label className="tw-text-black">Refund/Deposit Amount:</label>
            <div className='tw-bg-white tw-rounded tw-text-sm tw-py-2 tw-px-2 tw-border tw-my-2 '>
                <input className='tw-outline-0 tw-w-full' type="number" placeholder="Enter refund/deposit amount" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <button className="btn-primary-v2 tw-gap-x-1 tw-flex tw-items-center hover:tw-flex"
                disabled={!amount}
                onClick={handleRefund}
                type="button"
            >Submit {is_loading && <Loader size={16} className="tw-animate-custom-spin" />}
            </button>
        </div>
    )
}

function LoyaltyQRScannerPopup({ delivery_route, user, onClose }) {
    const video_ref = useRef(null);
    const scanner_ref = useRef(null);
    const scanner_ctx = useRef({}).current;
    const [is_loading, setIsLoading] = useState(true);
    const [is_submitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [scan_result, setScanResult] = useState(null);

    const destroyScanner = () => {
        scanner_ref.current?.destroy();
        scanner_ref.current = null;
    }

    const submitQRCode = (qr_content) => {
        if (scanner_ctx.is_submitting || scanner_ctx.has_result) return;

        scanner_ctx.is_submitting = true;
        scanner_ctx.has_result = true;
        setIsSubmitting(true);
        setError(null);
        scanner_ref.current?.stop();

        axios.post(
            `/api/qrscan?qr_key=${encodeURIComponent(qr_content)}`,
            {
                delivery_route_id: delivery_route._id,
                scan_for_user_id: user._id,
                reconfirm: true
            }
        ).then((resp) => {
            setScanResult(resp.data);
        }).catch((err) => {
            scanner_ctx.has_result = false;
            setError(err);
            scanner_ref.current?.start();
        }).finally(() => {
            scanner_ctx.is_submitting = false;
            setIsSubmitting(false);
        });
    }

    useEffect(() => {
        let is_mounted = true;

        loadScript("https://cdnjs.cloudflare.com/ajax/libs/qr-scanner/1.4.2/qr-scanner.legacy.min.js")
            .then(() => {
                if (!is_mounted || !video_ref.current) return;

                const QrScanner = window.QrScanner;
                if (!QrScanner) {
                    setError({"qr_scanner": "QR scanner could not be loaded"});
                    return;
                }

                scanner_ref.current = new QrScanner(
                    video_ref.current,
                    (result) => submitQRCode(result?.data || result),
                    {
                        preferredCamera: "environment",
                        returnDetailedScanResult: true,
                        maxScansPerSecond: 8,
                        highlightScanRegion: true,
                        highlightCodeOutline: true,
                        onDecodeError: () => {}
                    }
                );

                scanner_ref.current.start()
                    .then(() => setIsLoading(false))
                    .catch((err) => {
                        setIsLoading(false);
                        setError(err || {"camera": "Unable to access camera"});
                    });
            });

        return () => {
            is_mounted = false;
            destroyScanner();
        }
    }, []);

    const restartScanner = () => {
        scanner_ctx.has_result = false;
        setScanResult(null);
        setError(null);
        scanner_ref.current?.start();
    }

    return (
        <div className="tw-p-4 tw-text-sm">
            {
                scan_result
                    ? <div className="tw-text-center">
                        {
                            scan_result.errors && !scan_result.activated
                                ? <div className="tw-text-left"><GenericException ex={scan_result.errors} /></div>
                                : <div className="tw-bg-green-50 tw-border tw-border-green-100 tw-rounded tw-px-4 tw-py-6">
                                    <p className="tw-font-medium tw-text-green-700">Points availed successfully</p>
                                    <p className="tw-mt-2 tw-text-gray-600">
                                        {scan_result.points} points for {user.name}
                                    </p>
                                </div>
                        }
                        <div className="tw-mt-4 tw-flex tw-gap-x-2 tw-justify-center">
                            <button className="btn-tertiary" type="button" onClick={restartScanner}>Scan Again</button>
                            <button className="btn-primary-v2" type="button" onClick={onClose}>Done</button>
                        </div>
                    </div>
                    : <>
                        <div className="tw-relative tw-bg-black tw-rounded tw-overflow-hidden">
                            <video ref={video_ref} className="tw-w-full tw-min-h-[320px] tw-object-cover" muted playsInline />
                            {
                                (is_loading || is_submitting)
                                    ? <div className="tw-absolute tw-inset-0 flex-box tw-bg-black/60 tw-text-white">
                                        {is_submitting ? "Activating QR..." : "Starting camera..."}
                                    </div>
                                    : null
                            }
                        </div>
                        <p className="tw-mt-3 tw-text-center tw-text-gray-600">Scan loyalty QR for {user.name}</p>
                        {
                            error
                                ? <div className="tw-mt-3"><GenericException ex={error} /></div>
                                : null
                        }
                    </>
            }
        </div>
    )
}

function DeliveryRouteUser({ delivery_route, org, user, onUserUpdated }) {
    const [credit_wallet, setCreditWallet] = useState(user?.credit_wallet);
    const rerender = useRerender();
    const navigate = useNavigate();
    const ctx = useRef({}).current;
    const current_user = useCurrentUser();

    const showRefundPopUp = (user) => {
        var popup = Popup.show(
            "Refund/Deposit User",
            <RefundUser
                delivery_route={delivery_route}
                user={user}
                onSave={
                    (_user) => {
                        setCreditWallet(_user.credit_wallet)
                        popup.close();
                    }
                }
            />
        );
    }

    const scanLoyaltyQROnBehalf = () => {
        var popup = Popup.show(
            "Scan Loyalty QR",
            <LoyaltyQRScannerPopup
                delivery_route={delivery_route}
                user={user}
                onClose={() => popup.close()}
            />,
            { className: "tw-max-w-[520px]" }
        );
    }

    const addTags = (evt) => {
        ctx.selected_tags = null;
        /* show selecttags popup with user */
        const tags = [...new Set(delivery_route.delivery_points.map((cp) => cp.area).filter((area) => area)),
        ...new Set(delivery_route.data?.additional_tags?.map(tag => tag?.trim()).filter((tag) => tag))
        ]
        Popup.show(
            "Add Areas",
            <SelectTags
                tags={tags}
                selected_tags={user.tags}
                onSelectedTagsUpdated={(tags) => {ctx.selected_tags = tags}}
                className="w3-padding-8"
                placeholder_text="Select Areas"
            />,
            {
                "ok_button": "Update",
                "cancel_button": "Cancel",
                "cb": (action) => {
                    if (action) {
                        axios.post(
                            `/api/admin/user/${delivery_route._id}?action=update`,
                            {
                                "tags": ctx.selected_tags,
                                "user_id": user._id
                            }
                        ).then(
                            (resp) => {
                                if (resp.data.errors) {
                                    Popup.show("Errors", <GenericException ex={resp.data.errors} />)
                                    return;
                                }
                                EventTracker.track(PAGE_EVENTS.ADD_TAGS_USED_3DOT_SUCCESS);
                                user.tags = resp.data.updated_user?.tags;
                                rerender();
                            }
                        );
                    }
                }
            }
        );
    }

    const giveCredit = (evt) => {
        EventTracker.track(PAGE_EVENTS.GIVE_CREDITS_INITIATED);
        const popup = Popup.slideUp(
            <GiveCredit
                onUpdate={(credit_wallet) => { setCreditWallet(credit_wallet); popup.close() }}
                credit_wallet={credit_wallet}
                delivery_route_id={delivery_route._id}
                user_id={user._id}
            />,
            { title: 'Set Credit Limit' }
        )
    }

    const collectCreditDue = (evt) => {
        var popup = Popup.slideUp(
            <CollectCreditDue
                onUpdate={(credit_wallet) => { setCreditWallet(credit_wallet); popup.close() }}
                credit_wallet={credit_wallet}
                delivery_route_id={delivery_route._id}
                user_id={user._id}
            />,
            { title: 'Collect Credit Due (Cash)' }
        )
    }

    const viewLedger = () => {
        Popup.show("Ledger Entries", <WalletLedgerEntries wallet={credit_wallet} />);
    }

    const sendCreditStatement = (evt) => {
        axios.post(
            `/api/admin/delivery_route/${delivery_route._id}/user/${user._id}/send_credit_statement`
        ).then((resp) => {
            if (resp.data.errors) {
                Popup.show("Errors", <GenericException ex={resp.data.errors} />)
                return;
            }
            Popup.toast(<div>Credit statement sent!</div>, 2000)
        });
    }

    const removeUser = (evt) => {
        if (!window.confirm("Are you sure ?")) {
            return;
        }
        axios.post(
            `/api/admin/users/${delivery_route._id}?action=remove_user`,
            { "remove_user_id": user._id }
        ).then((resp) => {
            if (resp.data.removed) {
                user.is_deleted = true;
                rerender();
            }
        });
    }


    const editUser = () => {
        var popup = Popup.show(
            "Edit Customer",
            <AddNewCustomer
                delivery_route={delivery_route}
                user={user}
                onSave={
                    (_user) => {
                        Object.assign(user, _user);
                        onUserUpdated && onUserUpdated(_user);
                        rerender();
                        popup.close();
                    }
                }
            />
        );
    }

    const clearUnreadCount = () => {
        user.wa_user && (user.wa_user.cs_unseen_count = 0);
        rerender();
        broadcaster.broadcast_event(
            'users_unseen_count',
            delivery_route.users?.reduce(
                (count, user) => (
                    count + (user.wa_user?.cs_unseen_count || 0)
                ), 0
            )
        );
    }

    const onUpdateTags = (tags) => {
        user.tags = tags
        rerender();
    }

    const chatWithUser = async (evt) => {
        const { default: DeliveryRouteUserChatButtons } = await import(
            "./DeliveryRouteUserChatButtons"
        );
        const { default: CustomerTagsChatIndicator } = await import(
            "./CustomerTagsChatIndicator"
        );
        ChatSession.open(
            `wcs_${user._id}_${delivery_route.delivery_org.wa_business_number}`,
            {
                "bottomStatusIndicator": (session_data) => DeliveryRouteUserChatButtons.render(session_data, clearUnreadCount, delivery_route._id),
                "polling": true,
                "topIndicator": (session_data) => CustomerTagsChatIndicator.render(session_data, onUpdateTags, delivery_route._id)
            }
        )
    }

    const openCustomerNotes = () => {
        EventTracker.track(PAGE_EVENTS.USER_NOTES_INITIATED);
        ChatSession.open(
            `customer_notes_${delivery_route._id}_${user._id}`
        )
    }
    const openSurvey = () => {
        EventTracker.track(PAGE_EVENTS.TAKE_A_SURVEY);
        const popup = Popup.show("Survey",
            <SurveyPopup
                survey_questions={delivery_route.delivery_org?.config?.customer_notes_survey?.questions || []}
                onSelect={() => {
                    Popup.toast(<div>Survey Submitted Successfully!!</div>, 2000)
                    popup.close();
                }}
                delivery_route={delivery_route}
                user={user}
            />
        );
    }

    const isFinanceManager = (user, delivery_org_id) => {
        return user?.roles?.finance_manager && user?.roles?.finance_manager.includes(delivery_org_id)
    }

    // mirrors DeliveryRoute.is_manager on the backend (superadmin/staff, route/org manager, or finance manager)
    const isManager = (user, delivery_org_id) => {
        return !!(
            user?.roles?.superadmin
            || user?.roles?.staff
            || user?.roles?.manager?.includes(delivery_org_id)
            || user?.roles?.delivery_org_manager?.includes(delivery_org_id)
            || isFinanceManager(user, delivery_org_id)
        )
    }

    const chatOptions = () => (
        <div className='w3-list w3-list-bordered w3-pointer tw-text-sm'>
            <a className='tw-px-4 tw-py-2 w3-row'
                href={`/${delivery_route._id}?buying_for_user_id=${user._id}`}
                target="_blank" rel="noreferrer"
            >Send Cart</a>
            <div className='tw-px-4 tw-py-2' onClick={giveCredit}>Set Credit Limit</div>
            {
                credit_wallet?.due_amount > 0
                ?   <div className='tw-px-4 tw-py-2' onClick={collectCreditDue}>
                        Collect Credit Due (Cash)
                    </div>
                : null
            }
            {
                credit_wallet?.minimum_wallet_amount < 0
                ?   <div className='tw-px-4 tw-py-2' onClick={sendCreditStatement}>
                        Send Credit Statement
                    </div>
                : null
            }
            {
                credit_wallet
                ?   <div className='tw-px-4 tw-py-2' onClick={viewLedger}>
                        View Ledger
                    </div>
                : null
            }
            <a className="tw-px-4 tw-py-2 w3-block"
                href={`/admin/support/${user._id}/${delivery_route.delivery_org.wa_business_number}?delivery_route_id=${delivery_route._id}`}
                target="_blank"
                rel="noopener noreferrer"
            >Chat Support</a>
            <div className='tw-px-4 tw-py-2' onClick={addTags}>Add Tags</div>
            {
                isManager(current_user, delivery_route.delivery_org._id)
                    ? <div className='tw-px-4 tw-py-2' onClick={() => {
                        showRefundPopUp(user)
                    }}>Refund/Deposit</div>
                    : null
            }
            {
                org?.feature_flags?.loyalty_qr
                ?   <div className='tw-px-4 tw-py-2' onClick={scanLoyaltyQROnBehalf}>Scan Loyalty QR</div>
                :   null
                
            }
            <div className='tw-px-4 tw-py-2' onClick={openCustomerNotes}>User Notes</div>
            <div className='tw-px-4 tw-py-2' onClick={openSurvey}>Take a Survey</div>
            {onUserUpdated ? <div className='tw-px-4 tw-py-2 w3-text-orange' onClick={editUser}>Edit User</div> : null}
            <div className='tw-px-4 tw-py-2 w3-text-red' onClick={removeUser}>Remove User</div>
        </div>
    )

    const showOptions = (evt, is_popup) => {
        EventTracker.track(PAGE_EVENTS.DOT_MENU_CLICKS);
        if (is_popup) {
            Popup.slideUp(chatOptions())
        } else {
            Popup.showContextMenu(
                evt.target,
                chatOptions()
            );
        }
    };

    if (user.is_deleted || user.is_removed) {
        return null;
    }

    const now_millis = new Date().getTime();

    return (
        <div className='tw-flex md:tw-px-4 tw-py-2 tw-items-center hover:tw-bg-gray-100 tw-cursor-pointer' >
            <div className='tw-w-10 tw-h-10 tw-rounded-full flex-box tw-text-primary-v2 tw-bg-[#15956E0F] tw-overflow-hidden' onClick={chatWithUser}>
                {
                    !user.image
                        ? <span>{user.name?.slice(0, 1)}</span>
                        : <img alt={user.name} src={user.image} className='tw-object-cover' />
                }
            </div>
            <div className='tw-relative tw-ml-2 tw-grow tw-cursor-pointer' onClick={chatWithUser}>
                <div className='tw-text-sm tw-font-bold'>
                    {/* eslint-disable-next-line react/no-danger */}
                    <span dangerouslySetInnerHTML={{ __html: user.name }} />
                    {
                        user.session_user_type === SESSION_USER_ADMIN
                            ? <div className="tag-tertiary tw-m-1 tw-bg-black tw-text-white">ADMIN</div>
                            : null
                    }
                    {
                        user && Object.keys(user.status).length
                            ? <div
                                className="tag-tertiary tw-m-1 tw-text-white tw-py-1"
                                style={{ "backgroundColor": getRandomColor(Object.keys(user.status)[0], 0, STATUS_COLORS_LIST) }}
                            >
                                {Object.keys(user.status)[0]}&nbsp;{
                                    Object.values(user.status)[0]
                                        ?
                                        <DateView millis={Object.values(user.status)[0]} relative={true} />
                                        : null
                                }
                            </div>
                            : null
                    }
                    {
                        (user.wa_user?.notifications_paused_until || 0) > now_millis + 365 * MILLIS_IN_A_DAY
                        ? <div className="tw-inline-flex tw-items-center" title="No Notifications allowed" >
                                <MessageSquareXIcon size={14} className="tw-ml-1 tw-text-red-600"/>
                            </div>
                        : null
                    }
                </div>
                <div className='tw-text-xs tw-text-primary tw-my-1'>{user.phone_number || user.email_id}</div>
                {
                    credit_wallet
                        ? <div className='tw-text-xs tw-text-gray-600 tw-my-2 tw-flex tw-items-center tw-flex-wrap tw-divide-x'>
                            <span className="tw-pr-2">
                                {credit_wallet.wallet_amount <= 0 && credit_wallet.minimum_wallet_amount < 0 ? "Credit Used:" : "Wallet:"}&nbsp;
                                <span className={`${credit_wallet.wallet_amount === 0 ? 'tw-text-black' : credit_wallet.wallet_amount < 0 ? 'tw-text-red-600' : 'tw-text-green-600'} tw-text-sm`}>
                                    {credit_wallet.wallet_amount_str}
                                </span>

                            </span>
                            {
                                Math.abs(credit_wallet.minimum_wallet_amount) > 0
                                && <span className='tw-text-xs tw-px-2'>
                                    Credit: <span className="tw-text-xs">{credit_wallet.credit_amount_str}</span>
                                </span>
                            }
                            {/* Credit Due and Due Date hidden for now
                            {
                                Math.abs(credit_wallet?.due_amount) > 0
                                    ? <>
                                        <span className='tw-text-xs tw-px-2'>
                                            Credit Due: <span className="tw-text-red-600 tw-text-sm">{getPrice(credit_wallet.wallet_currency, credit_wallet.due_amount)} {credit_wallet.wallet_currency}</span>
                                        </span>
                                        <span className='tw-text-xs tw-pl-2'>
                                            Due Date: <DateView millis={credit_wallet.due_date} exclude_time={true} relative={true} />
                                        </span>
                                    </>
                                    : null
                            }
                            */}
                        </div>
                        : null
                }
                <div className='tw-text-xs tw-text-gray-600 tw-flex tw-items-center tw-flex-wrap tw-divide-x tw-space-x-1'>
                    {
                        user.delivery_org_data?.last_visited_at && (now_millis - user.delivery_org_data.last_visited_at) < 30 * MILLIS_IN_A_DAY
                            ? <div className='tw-text-xs tw-text-gray-600 tw-my-2 tw-flex tw-items-center tw-flex-wrap tw-divide-x'>
                                Visited:&nbsp;<DateView millis={user.delivery_org_data.last_visited_at} relative={true} />
                            </div>
                            : null
                    }
                    {
                        user.points_wallet
                            ? <div className="tw-w-fit tw-bg-white tw-p-1 tw-rounded-full tw-border tw-flex tw-items-center tw-text-xs tw-gap-1 tw-mb-2">
                                <img src={PointsIcon} style={{ width: 12 }} alt="points" /><span>{parseInt(user.points_wallet.wallet_amount, 10)}</span>
                            </div>
                            : null
                    }
                    {
                        user.score && user.score > 30
                        ?   <div className='tw-text-xs tw-text-gray-600 tw-mb-2 tw-flex tw-items-center tw-flex-wrap tw-pl-1'>
                                Score:&nbsp;
                                <span className='tw-text-green-600 tw-font-bold'>{user.score}</span>
                            </div>
                        :   null
                    }
                </div>
                {
                    user.tags?.length
                        ? <div className='tw-flex tw-flex-wrap tw-mb-1 tw-gap-1'>
                            {
                                user.tags.map(
                                    (tag) => <div key={tag} className='tag-tertiary'>{idToTitle(tag)}</div>
                                )
                            }
                        </div>
                        : null
                }
                {
                    user.wa_user?.cs_unseen_count
                        ? <div className='w3-display-right w3-flex w3-center'>
                            <div className='w3-circle w3-red w3-flex-vcenter tw-mr-2'
                                style={{ "width": "25px", "height": "25px" }}
                            >
                                {user.wa_user.cs_unseen_count}
                            </div>
                        </div>
                        : null
                }
            </div>
            <div className="tw-flex tw-gap-4 tw-items-center">
                <button className="btn-primary-v2 tw-gap-x-0.5 tw-flex tw-items-center hover:tw-flex" type="button" onClick={(e) => {
                    e.stopPropagation();
                    EventTracker.track(PAGE_EVENTS.CHAT_INITIATED_FROM_CUSTOMERS);
                    chatWithUser();
                    }}>
                    <MessageCirclePlus size={14} className="tw-mr-1" />
                    <span className="max-lg:tw-hidden">Open Chat</span>
                </button>
                <button className="md:tw-mr-3" onClick={showOptions} type="button">
                    <EllipsisVertical size={18}  className='tw-cursor-pointer' />
                </button>
            </div>
        </div>
    )
}
export default DeliveryRouteUser;
export {getDeliveryRouteUsersCached};
